import { sync } from 'vuex-router-sync';
import Vue from 'vue';
import { BootstrapVue, IconsPlugin } from 'bootstrap-vue';
import VeeValidate from 'vee-validate';
import VueVideoPlayer from 'vue-video-player';
import VueAnalytics from 'vue-analytics';
import VueCarousel from 'vue-carousel';
import moment from 'moment-timezone';
import VueMomentJS from 'vue-momentjs';
import VueWait from 'vue-wait';
import VueClipboard from 'vue-clipboard2';
import * as Sentry from '@sentry/vue';
import { Integrations } from '@sentry/tracing';

import 'bootstrap-vue/dist/bootstrap-vue.css';
import './assets/styles/main.scss';

import { GA_ID, SENTRY_DSN } from './config';
import AppRoot from './AppRoot';

import httpPlugin from './plugins/http';

import MyComponents from './components';
import MyFilters from './filters';
import router from './router';
import i18n from './i18n';
import store from './store';

Sentry.init({
  Vue,
  dsn: SENTRY_DSN,
  integrations: [
    new Integrations.BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracingOrigins: ['localhost', 'cl-viewer.classting.net', 'cl-viewer.classting.com', /^\//],
    }),
  ],
  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
    // reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Custom
    'Network request failed',
    'Failed to fetch',
    'Network Error',
    'NotAllowedError: play',
  ],
  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  sampleRate: 0.25,
  tracesSampleRate: 0.1,
});

Vue.config.productionTip = false;

Vue.use(VueClipboard);
Vue.use(VueWait);
Vue.use(BootstrapVue);
Vue.use(IconsPlugin);
Vue.use(VueCarousel);
Vue.use(VeeValidate, { errorBagName: 'veeErrors' });
Vue.use(httpPlugin, { store, router });
Vue.use(VueVideoPlayer);
Vue.use(VueAnalytics, {
  id: GA_ID,
  router,
  autoTracking: {
    pageviewOnLoad: false,
  },
  ignoreRoutes: ['auth'],
});

Vue.use(MyComponents);
Vue.use(MyFilters);
Vue.use(VueMomentJS, moment);

sync(store, router);

/* eslint-disable no-new */
new Vue({
  wait: new VueWait(),
  router,
  store,
  i18n,
  render: h => h(AppRoot),
}).$mount('#app');
